import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import axios from 'axios';
import axiosJWT from '../../secureAPI/axiosJWT';
import { StartLoading, StopLoading } from '../../Utils/loadingUtils';
import { showToast } from '../../Utils/toastUtils';
import { useParams } from 'react-router-dom';

// Registering ChartJS components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const OrderChart = () => {

  const { type } = useParams();


  const [selectedRange, setSelectedRange] = useState(10);  // Default to 10 days
  const [labels, setLabels] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [buyerOrderData, setBuyerOrderData] = useState([]);
  const [isLoading, setisLoading] = useState(false);
  // Fetch order data from API based on the selected date range
  const getChartOrderData = async (days) => {
    StartLoading(setisLoading);
    try {
      await axiosJWT.post(`${process.env.REACT_APP_API_URL}/api/GetChartData`, { days: days, app: 0 }, { withCredentials: true })
        .then((response) => {
          StopLoading(setisLoading);
          // console.log("API Response:", response.data);  // Log the response data
          if (response.data.success === 1) {
            // Check if response.data is an array
            if (Array.isArray(response.data.data)) {
              setOrderData(response.data.data);
              setBuyerOrderData(response.data.buyer)
              const dates = response.data.data.map(item => item.date);
              setLabels(dates);
            } else {
              console.error("Response data is not an array:", response.data.data);
            }
          }
          else {
            StopLoading(setisLoading)
            showToast(response.data.message, 'error');
          }
        })
        .catch((err) => {
          // console.log(err);
          if (err.response && err.response.status === 401) {
            // Handle unauthorized access
            // alert('Logout');
          } else if (err.response && err.response.data) {
            showToast(err.response.data.message, 'error');
          } else if (err.message) {
            showToast(err.message, 'error'); // e.g., "Refresh token missing or expired."
          } else {
            // showToast('An unexpected error occurredsssss.', 'error');
          }
          StopLoading(setisLoading);
        });

    } catch (err) {
      // console.log(err)
      StopLoading(setisLoading);
      showToast("Failed to Get Chart Data , please try again later ", 'error');
    }
  };


  // Fetch data whenever the selected date range changes
  useEffect(() => {
    getChartOrderData(selectedRange);
  }, [selectedRange]);

  // Function to map order data to chart data format
  const generateChartData = (user_type) => {
    const data = {
      NewOrder: [],
      OnHold: [],
      Processing: [],
      Packed: [],
      Dispatched: [],
      Completed: [],
      InTransit: [],
      Delivered: [],
    };

    if (user_type === "buyer") {

      buyerOrderData.forEach((entry) => {
        data.NewOrder.push(entry.NewOrder);
        data.OnHold.push(entry.OnHold);
        data.Processing.push(entry.Processing);
        data.Packed.push(entry.Packed);
        data.Dispatched.push(entry.Dispatched);
        data.Completed.push(entry.Completed);
        data.InTransit.push(entry.InTransit);
        data.Delivered.push(entry.Delivered);
      });

    }
    else {
      orderData.forEach((entry) => {
        data.NewOrder.push(entry.NewOrder);
        data.OnHold.push(entry.OnHold);
        data.Processing.push(entry.Processing);
        data.Packed.push(entry.Packed);
        data.Dispatched.push(entry.Dispatched);
        data.Completed.push(entry.Completed);
        data.InTransit.push(entry.InTransit);
        data.Delivered.push(entry.Delivered);
      });
    }


    return data;
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: 'New Order',
        data: generateChartData(type).NewOrder,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
      {
        label: 'On Hold',
        data: generateChartData(type).OnHold,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
      },
      {
        label: 'Processing',
        data: generateChartData(type).Processing,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
      {
        label: 'Packed',
        data: generateChartData(type).Packed,
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
      },
      {
        label: 'Dispatched',
        data: generateChartData(type).Dispatched,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
      },
      {
        label: 'Completed',
        data: generateChartData(type).Completed,
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
      },
      {
        label: 'In Transit',
        data: generateChartData(type).InTransit,
        backgroundColor: 'rgba(255, 205, 86, 0.6)',
      },
      {
        label: 'Delivered',
        data: generateChartData(type).Delivered,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: `Order Status for Last ${selectedRange} Days (Ending ${labels[labels.length - 1]})`,
      },
      legend: {
        position: 'top',
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Dates',
        },
        ticks: {
          maxRotation: 90,
          minRotation: 45,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Number of Orders',
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className=' m-2  lg:flex-row rounded-xl  p-3 shadow-[0px_10px_36px_0px_rgba(0,0,0,0.16),0px_0px_0px_1px_rgba(0,0,0,0.06)]'>


      {/* Dropdown to select date range */}
      <div className='flex justify-end'>

        <select onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); } }} onChange={(e) => setSelectedRange(Number(e.target.value))} value={selectedRange}>
          <option value={1}>Today</option>
          <option value={10}>Last 10 Days</option>
          <option value={15}>Last 15 Days</option>
          <option value={30}>Last 30 Days (1 Month)</option>
        </select>
      </div>

      {/* Render the bar chart */}
      {labels.length > 0 && <Bar data={chartData} options={options} />}
    </div>
  );
};

export default OrderChart;
